import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { protoInt64, Timestamp } from '@bufbuild/protobuf';
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraDateField, SpecraFormField, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { RegistryEntityPicker } from '../../../components/SharedRecord/RegistryEntityPicker';
import { registryRecordClient } from '../records/registryRecordClient';
import { seatErrorMessage } from './errors';
import { useSeatWrites } from './hooks';
import { seatLabel } from './model';
const T = Testids.peopleMovement;
const dateToTs = (iso)=>iso ? Timestamp.fromDate(new Date(`${iso}T00:00:00Z`)) : undefined;
const tsToDate = (ts)=>ts ? ts.toDate().toISOString().slice(0, 10) : '';
/** Shell shared by every seat dialog: a danger banner for a failed write, plus the footer. */ function SeatDialogShell({ title, testId, confirmLabel, destructive, confirmDisabled, pending, error, onConfirm, onClose, children }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (next)=>{
            if (!next && !pending) onClose();
        },
        title: title,
        testId: testId,
        footerProps: {
            confirmLabel,
            loading: pending,
            confirmDisabled,
            onConfirm,
            onCancel: onClose,
            confirmProps: destructive ? {
                variant: 'destructive'
            } : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-4)]",
            children: [
                children,
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    "data-testid": T.seatError,
                    children: error
                })
            ]
        })
    });
}
function SaveSeatDialog({ org, seats, state, onClose }) {
    const seat = state.kind === 'edit' ? state.seat : undefined;
    const { save } = useSeatWrites();
    const [parent, setParent] = useState(seat?.parentSeatId ?? '');
    const [created, setCreated] = useState(tsToDate(seat?.createdOn));
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const parentId = useId();
    const noteId = useId();
    const submit = ()=>{
        setError('');
        save.mutate({
            id: seat?.id ?? crypto.randomUUID(),
            employerKind: org.kind,
            employerId: org.id,
            parentSeatId: parent,
            sortOrder: seat?.sortOrder ?? 0,
            createdOn: dateToTs(created),
            sourceNote: note,
            graphRole: seat?.graphRole ?? '',
            expectedRevision: seat?.revision ?? protoInt64.zero
        }, {
            onSuccess: onClose,
            onError: (e)=>setError(seatErrorMessage(e))
        });
    };
    return /*#__PURE__*/ _jsxs(SeatDialogShell, {
        title: seat ? 'Edit seat' : 'Add seat',
        testId: T.seatDialog,
        confirmLabel: "Save",
        pending: save.isPending,
        error: error,
        onConfirm: submit,
        onClose: onClose,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "A seat takes its name from the job title of the person who holds it."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Reports to",
                htmlFor: parentId,
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    id: parentId,
                    value: parent,
                    onChange: (e)=>setParent(e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Top of the chart"
                        }),
                        seats.filter((s)=>s.id !== seat?.id).map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.id,
                                children: seatLabel(s)
                            }, s.id))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraDateField, {
                picker: "calendar",
                label: "Created",
                value: created,
                onValueChange: setCreated,
                clearable: true
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Source note",
                htmlFor: noteId,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    id: noteId,
                    value: note,
                    onChange: (e)=>setNote(e.target.value)
                })
            })
        ]
    });
}
function RetireSeatDialog({ state, onClose }) {
    const seat = state.kind === 'retire' ? state.seat : undefined;
    const { retire } = useSeatWrites();
    const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
    const [error, setError] = useState('');
    if (!seat) return null;
    return /*#__PURE__*/ _jsxs(SeatDialogShell, {
        title: "Retire seat",
        testId: T.retireDialog,
        confirmLabel: "Retire seat",
        pending: retire.isPending,
        confirmDisabled: !on,
        error: error,
        onClose: onClose,
        onConfirm: ()=>{
            setError('');
            retire.mutate({
                id: seat.id,
                retiredOn: dateToTs(on),
                expectedRevision: seat.revision
            }, {
                onSuccess: onClose,
                onError: (e)=>setError(seatErrorMessage(e))
            });
        },
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "body-ui",
                children: [
                    seatLabel(seat),
                    " stops appearing on the chart from this date. Its history stays."
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraDateField, {
                picker: "calendar",
                label: "Retired on",
                value: on,
                onValueChange: setOn
            })
        ]
    });
}
function DeleteSeatDialog({ state, onClose }) {
    const seat = state.kind === 'delete' ? state.seat : undefined;
    const { remove } = useSeatWrites();
    const [error, setError] = useState('');
    if (!seat) return null;
    return /*#__PURE__*/ _jsx(SeatDialogShell, {
        title: "Delete seat",
        testId: T.deleteDialog,
        confirmLabel: "Delete seat",
        destructive: true,
        pending: remove.isPending,
        error: error,
        onClose: onClose,
        onConfirm: ()=>{
            setError('');
            remove.mutate({
                id: seat.id
            }, {
                onSuccess: onClose,
                onError: (e)=>setError(seatErrorMessage(e))
            });
        },
        children: /*#__PURE__*/ _jsxs(SpecraText, {
            as: "p",
            size: "body-ui",
            children: [
                "Delete ",
                seatLabel(seat),
                "? A seat with people or sub-seats cannot be deleted; retire it instead, or move them first."
            ]
        })
    });
}
function AssignPersonDialog({ org, state, onClose }) {
    const seat = state.kind === 'assign' ? state.seat : undefined;
    const { assign } = useSeatWrites();
    const [contactId, setContactId] = useState('');
    const [employmentId, setEmploymentId] = useState('');
    const [error, setError] = useState('');
    const jobId = useId();
    const career = useQuery({
        queryKey: [
            'registry',
            'people-movement',
            'assign-career',
            contactId
        ],
        enabled: !!contactId,
        retry: false,
        queryFn: ()=>registryRecordClient.getPersonCareerPanel({
                contactId
            })
    });
    if (!seat) return null;
    const jobs = (career.data?.career ?? []).filter((e)=>e.employmentId && e.employer?.id === org.id);
    return /*#__PURE__*/ _jsxs(SeatDialogShell, {
        title: "Assign person",
        testId: T.assignDialog,
        confirmLabel: "Assign",
        pending: assign.isPending,
        confirmDisabled: !employmentId,
        error: error,
        onClose: onClose,
        onConfirm: ()=>{
            setError('');
            assign.mutate({
                employmentId,
                seatId: seat.id
            }, {
                onSuccess: onClose,
                onError: (e)=>setError(seatErrorMessage(e))
            });
        },
        children: [
            /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                kinds: [
                    'CONTACT'
                ],
                createMode: "none",
                label: "Person",
                value: contactId,
                onPick: (o)=>{
                    setContactId(o.registryId);
                    setEmploymentId('');
                }
            }),
            contactId && !career.isPending && jobs.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "No job is recorded for this person at this organization. Record one on their contact page first."
            }),
            jobs.length > 0 && /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Job",
                htmlFor: jobId,
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    id: jobId,
                    value: employmentId,
                    onChange: (e)=>setEmploymentId(e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose a job"
                        }),
                        jobs.map((j)=>/*#__PURE__*/ _jsxs("option", {
                                value: j.employmentId,
                                children: [
                                    j.title || 'Untitled job',
                                    " · ",
                                    tsToDate(j.startDate).slice(0, 4) || '?',
                                    "–",
                                    tsToDate(j.endDate).slice(0, 4) || 'now'
                                ]
                            }, j.employmentId))
                    ]
                })
            })
        ]
    });
}
/** The one open seat dialog, if any. Mount per open so each session starts from a fresh draft. */ export function SeatDialogs(props) {
    switch(props.state.kind){
        case 'add':
        case 'edit':
            return /*#__PURE__*/ _jsx(SaveSeatDialog, {
                ...props
            });
        case 'retire':
            return /*#__PURE__*/ _jsx(RetireSeatDialog, {
                ...props
            });
        case 'delete':
            return /*#__PURE__*/ _jsx(DeleteSeatDialog, {
                ...props
            });
        case 'assign':
            return /*#__PURE__*/ _jsx(AssignPersonDialog, {
                ...props
            });
    }
}
